import {
  type FileEditCompleteEdit,
  type FileEditCompleteImageEdit,
  type FileEditCompleteTextEdit,
  isImageEdit,
} from '@air/feature-ag-ui-chat';
import { reportErrorToBugsnag } from '@air/utils-error';

import { type AppNode, type NodePrompt } from '../../types';
import { getImageMetadata } from '../getImageMetadata';
import { computeRowAwareAppendPosition, type NodeRect } from '../layout/nodeLayout';

const DEFAULT_GROUP_DIMENSION = 1024;
const DEFAULT_TEXT_LAYER_WIDTH = 200;
const DEFAULT_LINE_HEIGHT_MULTIPLIER = 1.5;
const SMART_RESIZE_GROUP_KEY_PATTERN = /^(resize-\d+)/;

export const isTextEdit = (edit: FileEditCompleteEdit): edit is FileEditCompleteTextEdit =>
  'type' in edit && edit.type === 'text';

const getNormalizedAgentGroupKey = (groupId: string): string =>
  SMART_RESIZE_GROUP_KEY_PATTERN.exec(groupId)?.[1] ?? groupId;

const isAtGroupOrigin = (edit: FileEditCompleteEdit): boolean =>
  (edit.group?.x ?? 0) === 0 && (edit.group?.y ?? 0) === 0;

/**
 * An image at the group origin is the base. Offset layers are overlays.
 * When several images share that origin, the lowest zIndex wins. With no
 * origin image, the origin edit is the base.
 */
const getBaseEdit = (edits: FileEditCompleteEdit[]): FileEditCompleteEdit | undefined => {
  const baseImageEdit = edits.reduce<FileEditCompleteImageEdit | undefined>((current, edit) => {
    if (!isImageEdit(edit) || !isAtGroupOrigin(edit)) {
      return current;
    }

    if (!current) {
      return edit;
    }

    const zIndex = edit.group?.zIndex ?? Number.POSITIVE_INFINITY;
    const currentZIndex = current.group?.zIndex ?? Number.POSITIVE_INFINITY;

    return zIndex < currentZIndex ? edit : current;
  }, undefined);

  // No origin image. A text layer at (0, 0) still supplies the footprint.
  // With no origin edit, the caller falls back to the max extents.
  return baseImageEdit ?? edits.find(isAtGroupOrigin);
};

type ProcessGroupedEditsParams = {
  gap: number;
  groupedEdits: FileEditCompleteEdit[];
  maxRowWidth: number;
  prompt: NodePrompt;
  rects: NodeRect[];
  sourceNodeId?: string;
};

/**
 * A set of new nodes that the orchestrator should register as a server-side
 * group (via `createGroup`) once the nodes are synced. The local `groupId`
 * on each member is a temp UUID assigned in `processGroupedEdits` and
 * replaced with the server-assigned group node ID by the orchestrator.
 */
export type PendingAgentGroup = {
  nodeIds: string[];
};

export type ProcessGroupedEditsResult = {
  newNodes: AppNode[];
  pendingGroups: PendingAgentGroup[];
};

/**
 * Groups edits by their `group.id`, resolves image metadata, and builds
 * canvas-ready AppNode objects with correct positions and groupId assignments.
 *
 * Each group is placed as a unit on the canvas. The plate is the image at the
 * group origin; when several images share that origin, the lowest zIndex
 * wins. That layer determines the group footprint. Every other layer is sized
 * from its file. Layers are positioned relative to the group origin translated
 * to canvas coordinates.
 *
 * Nodes are assigned a temp UUID for `groupId` so the multi-select toolbar
 * shows group controls immediately. The orchestrator replaces the temp id
 * with a server-assigned one via `createGroupsForAgentGroupedEdits` once the
 * nodes are synced.
 */
export const processGroupedEdits = async ({
  gap,
  groupedEdits,
  maxRowWidth,
  prompt,
  rects,
  sourceNodeId,
}: ProcessGroupedEditsParams): Promise<ProcessGroupedEditsResult> => {
  // Group edits by agent group id.
  // Agent ids like "resize-0" may repeat across separate smart-resize runs, so we
  // map each incoming group to a unique local id to avoid merging with prior outputs.
  const groups = new Map<string, FileEditCompleteEdit[]>();
  const localGroupIdByAgentGroupId = new Map<string, string>();
  for (const edit of groupedEdits) {
    const agentGroupId = edit.group?.id;

    if (!agentGroupId) {
      continue;
    }
    const normalizedAgentGroupKey = getNormalizedAgentGroupKey(agentGroupId);
    const localGroupId =
      localGroupIdByAgentGroupId.get(normalizedAgentGroupKey) ?? `${normalizedAgentGroupKey}__${crypto.randomUUID()}`;
    localGroupIdByAgentGroupId.set(normalizedAgentGroupKey, localGroupId);
    const existing = groups.get(localGroupId);

    if (existing) {
      existing.push(edit);
    } else {
      groups.set(localGroupId, [edit]);
    }
  }

  const allNewNodes: AppNode[] = [];
  const pendingGroups: PendingAgentGroup[] = [];

  for (const [agentGroupId, edits] of groups) {
    const baseEdit = getBaseEdit(edits);

    // Resolve base layer dimensions for layout placement
    let groupWidth: number;
    let groupHeight: number;

    if (baseEdit && isImageEdit(baseEdit)) {
      try {
        const metadata = await getImageMetadata(baseEdit.url);
        groupWidth = metadata.width;
        groupHeight = metadata.height;
      } catch (error) {
        reportErrorToBugsnag({
          context: 'Failed to get image metadata for group base layer',
          error,
          metadata: { groupId: agentGroupId },
        });
        groupWidth = baseEdit.group?.width ?? DEFAULT_GROUP_DIMENSION;
        groupHeight = baseEdit.group?.height ?? DEFAULT_GROUP_DIMENSION;
      }
    } else {
      // Use group dimensions from the base layer, or find the largest reported dimensions
      groupWidth =
        baseEdit?.group?.width ?? edits.reduce((max, e) => Math.max(max, (e.group?.width ?? 0) + (e.group?.x ?? 0)), 0);
      groupHeight =
        baseEdit?.group?.height ??
        edits.reduce((max, e) => Math.max(max, (e.group?.height ?? 0) + (e.group?.y ?? 0)), 0);
    }

    const newNodeSize = { height: groupHeight, width: groupWidth };

    // Compute canvas position for the group (append to source node's row, or bottom-most)
    const { position: groupOrigin } = computeRowAwareAppendPosition({
      allNodes: rects,
      gap,
      maxRowWidth,
      newNodeSize,
      targetRowNodeId: sourceNodeId,
    });

    // Add the group footprint to rects so subsequent groups don't overlap
    rects.push({
      height: groupHeight,
      id: agentGroupId,
      width: groupWidth,
      x: groupOrigin.x,
      y: groupOrigin.y,
    });

    // Use a temp UUID for the local groupId so the multi-select toolbar shows
    // group controls immediately. The orchestrator will register a real
    // server-side group node and replace this with the server-assigned id.
    const tempGroupId = crypto.randomUUID();
    const groupNodeIds: string[] = [];

    // Build AppNodes for each layer in this group
    for (const [index, edit] of edits.entries()) {
      const nodeId = crypto.randomUUID();
      groupNodeIds.push(nodeId);
      const layerX = edit.group?.x ?? 0;
      const layerY = edit.group?.y ?? 0;
      const position = {
        x: groupOrigin.x + layerX,
        y: groupOrigin.y + layerY,
      };

      if (isTextEdit(edit)) {
        const { textNode } = edit;
        const layerWidth = edit.group?.width ?? DEFAULT_TEXT_LAYER_WIDTH;
        const layerHeight = edit.group?.height ?? textNode.fontSize * DEFAULT_LINE_HEIGHT_MULTIPLIER;

        const newNode: AppNode = {
          data: {
            baseWidth: layerWidth,
            fontColor: textNode.fontColor,
            fontFamily: textNode.fontFamily,
            fontSize: textNode.fontSize,
            source: 'agentic-edit',
            text: textNode.text,
            textAlign: textNode.textAlign,
            type: 'text',
            ...(textNode.fontVariant ? { fontVariant: textNode.fontVariant } : {}),
            ...(textNode.lineHeight != null ? { lineHeight: textNode.lineHeight } : {}),
            ...(textNode.letterSpacing != null ? { letterSpacing: textNode.letterSpacing } : {}),
            ...(textNode.fontSource ? { fontSource: textNode.fontSource } : {}),
            ...(textNode.fontFileUrl && textNode.fontSource === 'file' ? { fontFileUrl: textNode.fontFileUrl } : {}),
          },
          groupId: tempGroupId,
          id: nodeId,
          position,
          selected: false,
          style: { height: layerHeight, width: layerWidth },
          type: 'text',
          zIndex: edit.group?.zIndex,
        };

        allNewNodes.push(newNode);
      } else if (isImageEdit(edit)) {
        const isBaseLayer = edit === baseEdit;
        let imageWidth = edit.group?.width ?? groupWidth;
        let imageHeight = edit.group?.height ?? groupHeight;

        if (isBaseLayer) {
          imageWidth = groupWidth;
          imageHeight = groupHeight;
        } else {
          try {
            const metadata = await getImageMetadata(edit.url);
            imageWidth = metadata.width;
            imageHeight = metadata.height;
          } catch {
            // Fall back to group dimensions
          }
        }

        const newNode: AppNode = {
          data: {
            fileName: edit.fileName ?? '',
            media: { image: edit.url, original: edit.url },
            prompt: {
              messageId: prompt.messageId,
              runId: `${prompt.runId}-${index}`,
              text: prompt.text,
            },
            source: 'agentic-edit',
            type: 'image',
            ...(isBaseLayer && edit.smartResizeFormatId ? { smartResizeFormatId: edit.smartResizeFormatId } : {}),
            ...(!isBaseLayer ? { smartResizeSplitOverlay: true } : {}),
          },
          groupId: tempGroupId,
          id: nodeId,
          position,
          selected: false,
          style: { height: imageHeight, width: imageWidth },
          type: 'image',
          zIndex: edit.group?.zIndex,
        };

        allNewNodes.push(newNode);
      }
    }

    pendingGroups.push({ nodeIds: groupNodeIds });
  }

  return {
    newNodes: allNewNodes,
    pendingGroups,
  };
};
